import React, { useState } from 'react';
import {
  PhotoIcon,
  CameraIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  CalendarIcon,
  TagIcon,
  StarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { usePhotos } from '../hooks/usePhotos';
import {
  Photo,
  getPhotoUrl,
  formatFileSize,
} from '../api/photos';
import CreatePhotoModal from '../components/photos/CreatePhotoModal';
import EditPhotoModal from '../components/photos/EditPhotoModal';
import ViewPhotoModal from '../components/photos/ViewPhotoModal';
import ImageEditorModal from '../components/photos/ImageEditorModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';

const PAGE_SIZE_OPTIONS = [12, 24, 48, 96];

const Photos: React.FC = () => {
  const {
    photos,
    statistics,
    loading,
    error,
    total,
    page,
    limit,
    totalPages,
    setPage,
    setFilters,
    filters,
    createPhoto,
    updatePhoto,
    replacePhotoImage,
    deletePhoto,
    setAvatar,
    refreshData,
  } = usePhotos({ limit: 24 });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showImageEditorModal, setShowImageEditorModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Derive filter UI values from hook (with defaults)
  const searchTerm = filters.search ?? '';
  const selectedType = (filters.photoType ?? 'all') as 'all' | 'bird' | 'species' | 'general';
  const showAvatarsOnly = filters.isAvatar ?? false;
  const sortBy = (filters.sortBy ?? 'createdAt') as 'createdAt' | 'caption' | 'filename';
  const sortOrder = (filters.sortOrder ?? 'desc') as 'asc' | 'desc';

  const handleSearchChange = (value: string) => {
    setFilters({ search: value || undefined });
  };

  const handleTypeChange = (value: 'all' | 'bird' | 'species' | 'general') => {
    setFilters({ photoType: value === 'all' ? undefined : value });
  };

  const handleSortChange = (newSortBy: 'createdAt' | 'caption' | 'filename', newSortOrder?: 'asc' | 'desc') => {
    setFilters({ sortBy: newSortBy, sortOrder: newSortOrder ?? sortOrder });
  };

  const handleAvatarsOnlyChange = (checked: boolean) => {
    setFilters({ isAvatar: checked || undefined });
  };

  const handlePageSizeChange = (newLimit: number) => {
    setFilters({ limit: newLimit });
  };

  const handleCreatePhoto = async (formData: FormData) => {
    const result = await createPhoto(formData);
    if (result) {
      setShowCreateModal(false);
    }
  };

  const handleUpdatePhoto = async (data: any) => {
    if (selectedPhoto) {
      const result = await updatePhoto(selectedPhoto.id, data);
      if (result) {
        setShowEditModal(false);
        setSelectedPhoto(null);
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (selectedPhoto) {
      const result = await deletePhoto(selectedPhoto.id);
      if (result) {
        setShowDeleteModal(false);
        setSelectedPhoto(null);
      }
    }
  };

  const handleSetAvatar = async (photo: Photo) => {
    await setAvatar(photo.id);
  };

  const openEditModal = (photo: Photo) => {
    setSelectedPhoto(photo);
    setShowEditModal(true);
  };

  const openViewModal = (photo: Photo) => {
    setSelectedPhoto(photo);
    setShowViewModal(true);
  };

  const openDeleteModal = (photo: Photo) => {
    setSelectedPhoto(photo);
    setShowDeleteModal(true);
  };

  const openImageEditor = (photo: Photo) => {
    setSelectedPhoto(photo);
    setShowViewModal(false);
    setShowImageEditorModal(true);
  };

  const handleReplacePhotoImage = async (photoId: string, formData: FormData) => {
    await replacePhotoImage(photoId, formData);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading && photos.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading photos</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={refreshData}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <PageHero
        title="Photos"
        subtitle="Manage bird and species photos, avatars, and your gallery"
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Upload Photo
          </button>
        }
      />

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <PhotoIcon className="h-6 w-6 text-slate-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Photos</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CloudArrowUpIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Recent Uploads</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.recentUploads}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Storage Used</dt>
                    <dd className="text-lg font-medium text-slate-900">{formatFileSize(statistics.totalFileSize)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <StarIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Avatars</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.avatarCount}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="flex items-center w-full rounded-md border border-gray-300 bg-white focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500">
              <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
              <input
                type="text"
                className="flex-1 min-w-0 py-2 pr-3 border-0 bg-transparent leading-5 placeholder-gray-500 focus:outline-none focus:ring-0 focus:placeholder-gray-400 sm:text-sm"
                placeholder="Search photos..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label htmlFor="type-filter" className="block text-sm font-medium text-slate-700">
                  Photo Type
                </label>
                <select
                  id="type-filter"
                  value={selectedType}
                  onChange={(e) => handleTypeChange(e.target.value as any)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="all-1753461297390" value="all">All Photos</option>
                  <option key="bird-1753461297390" value="bird">Bird Photos</option>
                  <option key="species-1753461297390" value="species">Species Photos</option>
                  <option key="general-1753461297390" value="general">General Photos</option>
                </select>
              </div>

              <div>
                <label htmlFor="sort-by" className="block text-sm font-medium text-slate-700">
                  Sort By
                </label>
                <select
                  id="sort-by"
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value as any)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="createdAt-1753461297390" value="createdAt">Date Created</option>
                  <option key="caption-1753461297390" value="caption">Caption</option>
                  <option key="filename-1753461297390" value="filename">Filename</option>
                </select>
              </div>

              <div>
                <label htmlFor="sort-order" className="block text-sm font-medium text-slate-700">
                  Sort Order
                </label>
                <select
                  id="sort-order"
                  value={sortOrder}
                  onChange={(e) => handleSortChange(sortBy, e.target.value as 'asc' | 'desc')}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="desc-1753461297390" value="desc">Newest First</option>
                  <option key="asc-1753461297390" value="asc">Oldest First</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Options
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={showAvatarsOnly}
                    onChange={(e) => handleAvatarsOnlyChange(e.target.checked)}
                    className="rounded border-gray-300 text-cyan-600 shadow-sm focus:border-cyan-300 focus:ring focus:ring-cyan-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-slate-700">Avatars only</span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Photos Grid */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-medium text-slate-900">
            Photos {total > 0 ? `(${total} total)` : ''}
          </h3>
          {total > 0 && (
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <span>Per page</span>
                <select
                  value={limit}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="rounded-md border-gray-300 text-sm focus:border-cyan-500 focus:ring-cyan-500"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>

        {photos.length === 0 && !loading ? (
          <EmptyState
            className="mt-0 rounded-none border-0 shadow-none bg-transparent"
            title="No photos found"
            description={
              total === 0
                ? 'Get started by uploading your first photo.'
                : 'Try adjusting your search or filters.'
            }
            icon={<CameraIcon className="h-6 w-6" />}
            action={
              total === 0 ? (
                <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary text-sm focus-pop">
                  <PlusIcon className="h-4 w-4" />
                  Upload Photo
                </button>
              ) : undefined
            }
          />
        ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 p-6">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="relative group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openViewModal(photo)}
              >
                <div className="aspect-square">
                  <img
                    src={getPhotoUrl(photo.url)}
                    alt={photo.caption || 'Photo'}
                    className="w-full h-full object-cover bg-slate-100"
                    loading="lazy"
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (photo.id && !img.src.includes(`/photos/serve/${photo.id}`)) {
                        img.src = getPhotoUrl(photo.id);
                      }
                    }}
                  />
                  {photo.isAvatar && (
                    <div className="absolute top-2 right-2">
                      <StarIcon className="h-5 w-5 text-yellow-400 fill-current" />
                    </div>
                  )}
                </div>

                <div className="p-3">
                  {photo.caption && (
                    <h4 className="text-sm font-medium text-slate-900 truncate mb-1">
                      {photo.caption}
                    </h4>
                  )}
                  
                  <div className="flex items-center text-xs text-slate-500 space-x-2">
                    <div className="flex items-center">
                      <CalendarIcon className="h-3 w-3 mr-1" />
                      {formatDate(photo.createdAt)}
                    </div>
                  </div>

                  {photo.tags && photo.tags.length > 0 && (
                    <div className="flex items-center mt-2">
                      <TagIcon className="h-3 w-3 text-slate-400 mr-1" />
                      <div className="flex flex-wrap gap-1">
                        {photo.tags.slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            className="px-1 py-0.5 text-xs bg-gray-100 text-slate-600 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                        {photo.tags.length > 2 && (
                          <span className="text-xs text-slate-500">
                            +{photo.tags.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Hover actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex space-x-2">
                    {!photo.isAvatar && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetAvatar(photo);
                        }}
                        className="p-2 bg-white rounded-full shadow-lg hover:bg-slate-50/80"
                        title="Set as Avatar"
                      >
                        <StarIcon className="h-4 w-4 text-slate-600" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openImageEditor(photo);
                      }}
                      className="p-2 bg-white rounded-full shadow-lg hover:bg-slate-50/80"
                      title="Edit image"
                    >
                      <svg className="h-4 w-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(photo);
                      }}
                      className="p-2 bg-white rounded-full shadow-lg hover:bg-slate-50/80"
                      title="Edit details"
                    >
                      <svg className="h-4 w-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(photo);
                      }}
                      className="p-2 bg-white rounded-full shadow-lg hover:bg-slate-50/80"
                      title="Delete"
                    >
                      <svg className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination - show whenever there are results (including when filters apply and only 1 page) */}
          {total > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-slate-700">
                Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
                <span className="font-medium">{Math.min(page * limit, total)}</span> of{' '}
                <span className="font-medium">{total}</span> photos
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  <ChevronLeftIcon className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <span className="text-sm text-slate-600 px-2">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* Modals */}
      <CreatePhotoModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreatePhoto}
      />

      <EditPhotoModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedPhoto(null);
        }}
        onSubmit={handleUpdatePhoto}
        photo={selectedPhoto}
        onEditImage={
          selectedPhoto
            ? () => {
                setShowEditModal(false);
                openImageEditor(selectedPhoto);
              }
            : undefined
        }
      />

      <ViewPhotoModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedPhoto(null);
        }}
        photo={selectedPhoto}
        onEdit={() => {
          setShowViewModal(false);
          setShowEditModal(true);
        }}
        onEditImage={() => {
          if (selectedPhoto) openImageEditor(selectedPhoto);
        }}
        onDelete={() => {
          setShowViewModal(false);
          setShowDeleteModal(true);
        }}
        onSetAvatar={handleSetAvatar}
      />

      <ImageEditorModal
        isOpen={showImageEditorModal}
        onClose={() => {
          setShowImageEditorModal(false);
          setSelectedPhoto(null);
        }}
        photo={selectedPhoto}
        onSave={handleReplacePhotoImage}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedPhoto(null);
        }}
        onConfirm={handleDeletePhoto}
        title="Delete Photo"
        message={`Are you sure you want to delete this photo? This action cannot be undone.`}
      />
    </div>
  );
};

export default Photos; 